import { ITableClt } from "@/app/services/cltService/getTables";
import { cn } from "@/app/utils/cn";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { GrayTableCard } from "@/view/components/GrayTableCard";
import { addMonths, format } from "date-fns";
import { useMemo } from "react";
// import { format } from "date-fns";

interface OfferCardProps {
  offer: ITableClt;
  selected: boolean;
  onClick: () => void;
}

export function OfferCard({ offer, selected, onClick }: OfferCardProps) {
  const title = offer.NomeTabela
    ? `${offer.NomeTabela} - ${offer.Prazo}x`
    : (offer.tableName ?? "Grandino CLT - CONSIGNADO PRIVADO");

  const infos = [
    {
      label: "Valor liberado",
      value: addMoneySimbols(offer.ValorSolicitado || 0),
    },
    {
      label: "Valor principal",
      value: addMoneySimbols(offer.ValorFinanciado || 0),
    },
    { label: "Total", value: addMoneySimbols(offer.ValorBruto || 0) },
    { label: "IOF", value: addMoneySimbols(offer.ValorIOC || 0) },
    { label: "Seguro", value: addMoneySimbols(offer.ValorSeguro || 0) },
  ];

  const { Prazo, ValorParcela, DataPrimeiroVencimento, DataUltimoVencimento } =
    offer;

  const parcelas = useMemo(() => {
    if (!Prazo || !DataPrimeiroVencimento) return [];

    const firstDate = new Date(DataPrimeiroVencimento);
    const lastDate = DataUltimoVencimento
      ? new Date(DataUltimoVencimento)
      : null;

    const list = Array.from({ length: Prazo }, (_, index) => {
      let dueDate = addMonths(firstDate, index);

      if (index === Prazo - 1 && lastDate) {
        dueDate = lastDate;
      }

      return {
        num: index + 1,
        dueDate: format(dueDate, "MM/yyyy"),
        value: ValorParcela,
      };
    });

    return list;
  }, [Prazo]);

  const taxas = [
    ["AP", offer.TaxaAP_AM, offer.TaxaAP_AA],
    ["Nominal", offer.TaxaNominal_AM, offer.TaxaNominal_AA],
    ["Cliente", offer.TaxaCliente_AM, offer.TaxaCliente_AA],
  ];
  return (
    <div
      onClick={onClick}
      className={cn(
        "flex w-full cursor-pointer  flex-col gap-5 rounded-2xl border p-5 md:min-w-[500px] md:max-w-[600px]",
        selected && "border-primary shadow-md shadow-primary",
      )}
    >
      <h1 className="font-semibold">{title}</h1>

      <div className="flex items-center justify-between gap-4 rounded-[10px] bg-primary px-[18px] py-3">
        <span className="text-sm font-bold text-white">Liberado</span>
        <span className="text-xl font-extrabold text-white">
          {addMoneySimbols(offer.ValorLiquidoCliente || 0)}
        </span>
      </div>

      <div className="flex flex-col gap-4 rounded-lg border border-primary p-2.5">
        <div className="flex justify-between gap-5 max-md:gap-3 max-sm:border-b-0">
          <div>
            <p className="text-xs font-medium text-black04">Liberado</p>
            <span className="font-medium max-sm:text-sm">
              {addMoneySimbols(offer.ValorLiquidoCliente || 0)}
            </span>
          </div>
          <div className="text-center">
            <p className="text-xs font-medium text-black04">
              Comissão ({offer.commission ?? offer.PorcentagemComissao}%
              {(offer.bonus || 0) > 0 && ` + ${offer.bonus}%`})
            </p>
            <span className=" font-medium max-sm:text-sm">
              {addMoneySimbols(
                (offer.ValorLiquidoCliente *
                  (offer.commission ?? offer.PorcentagemComissao)) /
                  100 || 0,
              )}
            </span>
          </div>
          <div className="text-right">
            <p className=" text-xs font-medium text-black04">Valor parcelas</p>
            <span className="font-medium max-sm:text-sm">
              {addMoneySimbols(offer.ValorParcela || 0)}
            </span>
          </div>
        </div>

        <div className="flex justify-between gap-5  max-md:gap-3 max-sm:border-b-0">
          <div>
            <p className="text-xs font-medium text-black04">
              Previsão da primeira parcela
            </p>
            <span className="font-medium max-sm:text-sm">
              {format(new Date(offer?.DataPrimeiroVencimento), "MM/yyyy")}
            </span>
          </div>
          <div className="text-right">
            <p className="text-xs font-medium text-black04">
              Previsão da última parcela
            </p>
            <span className="font-medium max-sm:text-sm">
              {format(new Date(offer?.DataUltimoVencimento), "MM/yyyy")}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-2.5">
        {infos.map((info) => (
          <div
            key={info.label}
            className="flex items-center justify-between gap-4"
          >
            <p className="text-xs font-medium text-black04">{info.label}</p>
            <span className="text-sm font-medium">{info.value}</span>
          </div>
        ))}
      </div>
      <GrayTableCard
        hasVerticalScroll={true}
        title={`Parcelas ${Prazo}x`}
        headers={["N°", "Vencimento", "Valor"]}
        rows={parcelas.map((parcela) => [
          parcela.num,
          parcela.dueDate,
          addMoneySimbols(parcela.value),
        ])}
      />

      <GrayTableCard
        title="Taxas"
        headers={["Nome", "Mensal(AM)", "Anual(AA)"]}
        rows={taxas}
      />
    </div>
  );
}
